<template>
  <div
    class="tooltip inline-block relative font-normal text-base flex flex-1 items-center"
    :class="{
      'cursor-help': message || warnings || image,
      'border-dotted border-b-2 border-gray-500':
        (message || warnings || image) && showDottedLine,
    }"
  >
    <div
      v-if="message || warnings || image"
      class="inner-container hidden absolute z-50 bottom-full transform mb-2"
      :class="{
        'w-64': extendWidth,
        'w-auto': !extendWidth,
        '-left-5': isRight,
        '-right-5': isLeft,
        'left-1/2 -translate-x-1/2': image,
      }"
    >
      <div class="relative mx-2">
        <div
          class="bg-black text-white text-xs rounded right-0 bottom-full text-left p-2"
        >
          <template v-if="message">
            <div v-if="isMessageArray && message.length > 0">
              <table>
                <tr v-for="item in message" :key="item.key">
                  <td class="text-right pr-2 whitespace-nowrap">
                    {{ item.key || '' }}
                  </td>
                  <td class="text-left pl-2 whitespace-nowrap">
                    {{ methods.formatValue(item.value) }}
                  </td>
                </tr>
              </table>
            </div>
            <template v-else>
              {{ message }}
            </template>
          </template>
          <div v-if="warnings" class="w-64 text-left">
            <p v-for="item in warnings" :key="item" class="mb-1">
              {{ item }}
            </p>
          </div>
          <div v-if="image" class="img-container">
            <img :src="`data:image;base64,${image}`" />
          </div>
          <svg
            class="absolute h-3 top-full"
            :class="{
              'left-3.5': isRight,
              'right-3.5': isLeft,
              'left-1/2 transform -translate-x-1/2': image,
            }"
            viewBox="0 0 255 255"
          >
            <polygon points="0,0 127.5,127.5 255,0" />
          </svg>
        </div>
      </div>
    </div>
    <div class="font-bold">
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import { DataValue, TooltipMessage } from '@/types/Global';
import { formatDataValue } from '@/helpers/format';
import { Direction } from '@/enums/Global';

export default defineComponent({
  name: 'Tooltip',
  props: {
    message: {
      type: [Array, String] as PropType<TooltipMessage | null>,
      default: null,
    },
    warnings: {
      type: Array as PropType<string[]>,
      default: null,
    },
    showDottedLine: {
      type: Boolean,
      default: false,
    },
    extendWidth: {
      type: Boolean,
      default: false,
    },
    direction: {
      type: String as PropType<Direction>,
      default: Direction.RIGHT,
    },
    image: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const isMessageArray = computed(() => Array.isArray(props.message));

    const isRight = computed(() => props.direction === Direction.RIGHT);
    const isLeft = computed(() => props.direction === Direction.LEFT);

    const formatValue = (dataValue: DataValue) => {
      if (typeof dataValue === 'object') {
        return formatDataValue(dataValue);
      }
      return dataValue;
    };
    return { isMessageArray, isRight, isLeft, methods: { formatValue } };
  },
});
</script>

<style lang="postcss" scoped>
.img-container {
  width: 42rem;
}
</style>
